import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuthStore } from './stores/authStore';
import './stores/themeStore'; // side-effect: applies dark class to <html>
import apiService from './services/apiService';
import analyticsService from './services/analyticsService';

// Initialize analytics
analyticsService.init();

// Initialize auth store
useAuthStore.getState().hydrate();

// Wire apiService token refresh/logout callbacks to Zustand store
apiService.setAuthCallbacks(
  (newToken) => useAuthStore.getState().setToken(newToken),
  () => useAuthStore.getState().logout()
);

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds — data considered fresh for 30s, then refetches on next access
      gcTime: 5 * 60 * 1000, // 5 minutes — keep inactive data in cache for 5 min before garbage collecting
      retry: 1,
      refetchOnWindowFocus: true, // refetch when user switches back to tab
    },
  },
});

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <App />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
        </BrowserRouter>
      </GoogleOAuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
);
